<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-select
                        show-default-option
                        label="Tipo"
                        text-field="descricao"
                        value-field="id"
                        :options="form.tipos"
                        v-model="model.tipoId"
                        :validations="validations.tipo"
                    />
                </rkm-col>

                <rkm-col cols="8">
                    <rkm-form-input
                        label="Nome"
                        v-model="model.nome"
                        :validations="validations.nome"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-input-autocomplete
                        :searchFields="form.autocompleteFields.bairro"
                        :repository="repositories.bairro"
                        v-model="model.bairro"
                        :validations="validations.bairro"
                        label="Bairro"
                        uppercase
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                color="primary"
                                v-rkm-tooltip.hover
                                title="Cadastrar bairro"
                                @click.stop="$refs.ModalBairro.open()"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        label="CEP"
                        placeholder="99.999-999"
                        v-model="model.cep"
                        v-mask="mask.cep"
                        :validations="validations.cep"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        show-default-option
                        label="Estado"
                        text-field="uf"
                        value-field="id"
                        :options="form.estados"
                        v-model="model.estadoId"
                        :validations="validations.estado"
                    />
                </rkm-col>

                <rkm-col cols="9">
                    <rkm-form-input-autocomplete
                        :searchFields="form.autocompleteFields.municipio"
                        :repository="repositories.municipio"
                        :requestQueryStringParams="{ estado_id: model.estadoId }"
                        v-model="model.municipio"
                        :validations="validations.municipio"
                        label="Município"
                        :disabled="!model.estadoId"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <modal-bairro
            ref="ModalBairro"
            @save="model.bairro = $event"
        />
    </div>
</template>

<script>
// Components.
import ModalBairro from '@/components/cadastro/bairro/Modal'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Mixins.
import { repository, mask, unmask, stringCase, object, error, importacaoCns } from '@/mixins'

export default {
    components: { ModalBairro },
    mixins: [ repository, mask, unmask, stringCase, object, error, importacaoCns ],
    props: {
        id: { type: Number }
    },
    data: () => ({
        repositories: {
            logradouro: null,
            municipio: null,
            bairro: null,
        },
        form: {
            termosInvalidos: [],
            tipos: [],
            estados: [],
            autocompleteFields: {
                bairro: [
                    { key: 'nome', searchable: true },
                ],
                municipio: [
                    { key: 'nome', searchable: true },
                ],
            },
        },
        model: {
            tipoId: null,
            bairro: null,
            estadoId: null,
            municipio: null,
            cep: '',
            nome: '',
        },
    }),
    computed: {
        validations() {

            return {
                nome: {
                    required: true,
                    max: 250,
                    min: 1,
                    regex: this.importacaoCns.regexValidacao,
                    termo_invalido: this.form.termosInvalidos,
                },
                cep: { required: true, min_max_without_mask: 8 },
                tipo: { required: true },
                estado: { required: true },
                bairro: { required: true },
                municipio: { required: true },
            }
        },
        ...mapGetters({ cliente: 'getCliente' }),
    },
    watch: {
        'model.estadoId': function() {

            this.model.municipio = null
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const request = this.id
                ? this.repositories.logradouro.edit(this.id)
                : this.repositories.logradouro.create()

            request
                .then(({ data }) => {

                    this.form.termosInvalidos = data.form.termosInvalidos
                    this.form.tipos = data.form.tipos
                    this.form.estados = data.form.estados

                    if (data.model) {

                        this.model.tipoId = data.model.tipoId
                        this.model.nome = data.model.nome
                        this.model.bairro = data.model.bairro
                        this.model.cep = data.model.cep
                        this.model.estadoId = data.model.estadoId

                        this.$nextTick(() => this.model.municipio = data.model.municipio)
                    }
                    else {

                        this.model.estadoId = this.cliente.municipio.estado.id

                        this.$nextTick(() => this.model.municipio = this.cliente.municipio)
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    const request = this.id
                        ? this.repositories.logradouro.update(this.id, this.prepareData())
                        : this.repositories.logradouro.store(this.prepareData())

                    request
                        .then(({ data }) => {

                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const logradouro = this.clone(this.model)

            logradouro.cep = this.unMaskCep(logradouro.cep)

            logradouro.bairroId = logradouro.bairro.id
            logradouro.municipioId = logradouro.municipio.id

            delete logradouro.bairro
            delete logradouro.municipio

            return this.camelObjectKeysToSnakeRecursive(logradouro)
        },
    },
    created() {

        this.repositories.logradouro = this.getConectaSusRepository('cadastro.localizacao.logradouro')
        this.repositories.municipio = this.getConectaSusRepository('intranet.municipio')
        this.repositories.bairro = this.getConectaSusRepository('cadastro.localizacao.bairro')

        this.load()
    }
}
</script>